import {
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { colors } from '../../../../theme/mui/colors';
import ClientCareIcon from '../../../../components/v2/icons/ClientCareIcon';
import EmailIcon from '../../../../components/v2/icons/EmailIcon';
import PhoneCallIcon from '../../../../components/v2/icons/PhoneCallIcon';
import CloseIcon from '../../../../components/v2/icons/CloseIcon';
import { useState } from 'react';
import PlusIcon from '../../../../components/v2/icons/PlusIcon';
import { cloneDeep } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import Queries from '../../../common/queries';
import { IArticle, SupportApi } from '../../../../api';
import { useLocalization } from '../../../../localization';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import ReplayIcon from '@mui/icons-material/Replay';

const HelpCenter = () => {
  //#region Properties

  const theme = useTheme();
  const { t, locale } = useLocalization();

  const supportItems = [
    {
      title: t('helpCenter.clientCare'),
      subTitle: t('helpCenter.clientCareDescription'),
      icon: <ClientCareIcon />,
      via: {
        email: 'info.dk@efkt.com',
        telephone: '(+45) 70 222 466',
      },
    },
  ];

  // const { control, setValue, getValues } = useForm({
  //   defaultValues: {
  //     firstName: '',
  //     lastName: '',
  //     email: '',
  //     phoneNumber: '',
  //     visiblePrices: false,
  //   },
  //   mode: 'onChange',
  //   values: {},
  // });

  const [idToFaqExpanded, setIdToFaqExpanded] = useState<Record<string, boolean>>({});

  const {
    data: faqs = [],
    isLoading: areFaqsLoading,
    refetch: refetchFaqs,
    isError: hasFaqError,
    isRefetching: areFaqsRefetching,
  } = useQuery({ queryKey: [Queries.supportArticles, locale], queryFn: () => SupportApi.getArticles(locale) });

  //#endregion

  //#region Methods
  const isLoading = areFaqsLoading || areFaqsRefetching;

  function toggleFaqExpansion(id: string): void {
    const currentIdToFaqExpanded = cloneDeep(idToFaqExpanded);
    currentIdToFaqExpanded[id] = !currentIdToFaqExpanded[id];
    setIdToFaqExpanded(() => currentIdToFaqExpanded);
  }

  function drawFaq({ loading, faq }: { loading?: boolean; faq: IArticle }) {
    return (
      <Card key={faq.id} style={{ padding: '1.5rem' }}>
        <CardContent style={{ padding: 0 }}>
          <Stack direction={'row'}>
            <Stack flex={'1 1 auto'} paddingRight={'9rem'} justifyContent={'center'}>
              <Typography variant="h5" lineHeight={'120%'}>
                {loading ? <Skeleton width={'20%'} /> : faq.title}
              </Typography>
              {idToFaqExpanded[faq.id] && (
                <div style={{ marginTop: '1rem' }} dangerouslySetInnerHTML={{ __html: faq.content }} />
              )}
            </Stack>
            <Stack flex={'0 0 auto'} justifyContent={'center'} alignItems={'center'}>
              {!isLoading && (
                <IconButton onClick={() => toggleFaqExpansion(faq.id)}>
                  {idToFaqExpanded[faq.id] ? (
                    <CloseIcon color={colors.red['500']} />
                  ) : (
                    <PlusIcon color={colors.red['500']} />
                  )}
                </IconButton>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  function drawFaqs() {
    const isComponentLoading = areFaqsLoading || areFaqsRefetching;
    if (isComponentLoading) {
      const skeletalFaqs: IArticle[] = [
        { id: '1', title: '', content: '', description: '' },
        { id: '2', title: '', content: '', description: '' },
      ];

      return skeletalFaqs.map(skeletalFaq =>
        drawFaq({
          faq: skeletalFaq,
          loading: isComponentLoading,
        })
      );
    }

    if (hasFaqError) {
      return (
        <Stack>
          <Alert
            icon={false}
            variant="outlined"
            severity="error"
            action={
              <IconButton size="small" color="inherit" onClick={async () => await refetchFaqs()}>
                <ReplayIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {t('common.messages.faqFailedToLoadPleaseTryAgain')}
          </Alert>
        </Stack>
      );
    }

    if (faqs.length < 1) {
      return (
        <Stack>
          <Alert
            icon={false}
            variant="outlined"
            severity="warning"
            action={
              <IconButton size="small" color="inherit" onClick={async () => await refetchFaqs()}>
                <ReplayIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {t('common.messages.noFaqFoundInTheSystem')}
          </Alert>
        </Stack>
      );
    }

    return faqs.map(faq => drawFaq({ faq, loading: isComponentLoading }));
  }

  //#endregion

  return (
    <div>
      {isLoading && <LinearProgress style={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
      <Stack direction={'column'}>
        <Stack direction={'row'} marginBottom={'1.5rem'}>
          <Stack flex={'1 1 auto'}>
            <Typography variant={'h4'} lineHeight={'120%'}>
              {t('helpCenter.contactUs')}
            </Typography>
            <Typography variant={'body1'} lineHeight={'150%'} color={theme.palette.grey['500']}>
              {t('helpCenter.reachOutUsingDifferentOptionsOrFillOutTicketRequest')}
            </Typography>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          {supportItems.map((item, index) => (
            <Grid size={{ md: 4, xs: 12 }} key={index}>
              <Card style={{ padding: 0 }}>
                <Stack bgcolor={colors.base.level20} height={'8.75rem'} alignItems={'center'} justifyContent={'center'}>
                  {item.icon}
                </Stack>
                <Stack padding={'1.25rem'}>
                  <Typography variant={'h5'} lineHeight={'120%'}>
                    {item.title}
                  </Typography>
                  <Typography
                    paddingTop={'0.5rem'}
                    variant={'body2'}
                    lineHeight={'120%'}
                    color={theme.palette.grey['500']}
                  >
                    {item.subTitle}
                  </Typography>
                  <Stack
                    paddingTop={'1.25rem'}
                    direction={{ xs: 'column', md: 'column', lg: 'row' }}
                    alignItems={'center'}
                    justifyContent={'center'}
                    gap={'1rem'}
                    flexWrap={{ xs: 'wrap', lg: 'nowrap' }}
                  >
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      component="a"
                      href={['mailto', item.via.email].join(':')}
                    >
                      <Typography variant={'h5'} color={colors.base.level70}>
                        {t('helpCenter.sendEmail')}
                      </Typography>
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<PhoneCallIcon />}
                      component="a"
                      href={['tel', item.via.telephone].join(':')}
                    >
                      <Typography variant={'h5'} color={colors.base.level70}>
                        {t('helpCenter.callUs')}
                      </Typography>
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* TODO: At the moment of writing this, the feature has not documented anywhere */}
        {/* <Divider sx={{ marginY: '2.5rem' }} /> */}
        {/* <Typography variant="h4" lineHeight={'120%'}>
          {t('helpCenter.sendATicketRequest')}
        </Typography>
        <Typography variant={'body1'} color={theme.palette.grey['500']} paddingRight={'18.75rem'}>
          {t('helpCenter.sendTicketRequestDescription')}
        </Typography>

        <Stack style={{ paddingTop: '2.5rem' }} gap={'1.25rem'}>
          <InputField
            control={control} name="firstName" label={t('helpCenter.yourName')} disabled={isLoading} required />

          <InputField
            control={control} name="firstName" label={t('helpCenter.email')} disabled={isLoading} required />

          <InputPhoneNumber name="phoneNumber" label={t('helpCenter.phoneNumber')} control={control} disabled={isLoading} required />

          <TextAreaInputField
            rows={10}
            required
            label="helpCenter.messageRequest"
            name="importantElement"
            control={control}
          />

          <Stack alignItems="end">
            <Button style={{ height: '3.5rem', color: colors.base.white }} type="submit" variant="contained" disabled={isLoading}>
              {t('helpCenter.sendTicketRequest')}
            </Button>
          </Stack>
        </Stack> */}

        <Divider sx={{ marginY: '2.5rem' }} />

        <Typography variant={'h4'} lineHeight={'120%'}>
          {t('helpCenter.frequentlyAskedQuestions')}
        </Typography>

        <Stack marginTop={'2.5rem'} spacing={'0.75rem'}>
          {drawFaqs()}
        </Stack>
      </Stack>
    </div>
  );
};

export default HelpCenter;
